// FormenDisplayPage.jsx — die Leinwand fuer die Formen-Wertung.
//
// Frueher war das ein window.open('') aus dem Schreiber-Panel, in das per
// innerHTML fuenf verschiedene Layouts geschrieben wurden. Das hiess: nur als
// Popup desselben Browsers auf demselben Rechner, bei jedem Tastendruck ein
// kompletter DOM-Neuaufbau (das Flackern auf der Leinwand), und nach einem
// Reload des Schreiber-Fensters blieb die Leinwand leer.
//
// Jetzt eine eigene Seite mit fuenf Zustaenden und einem Stylesheet. Die
// Steuerung kommt ueber localStorage (storage-Event) und BroadcastChannel —
// dasselbe Muster wie bei der Siegerehrung.
import React, { useState, useEffect } from 'react';
import './FormenDisplayPage.css';
// Das Platzabzeichen teilen sich alle Leinwaende.
import Platz from '../components/leinwand/Platz';
import Bild from '../components/Bild';
import Verbindung from '../components/leinwand/Verbindung';
import { useAnimierteListe } from '../hooks/useHervorheben';
import { useLeinwandVomServer, leinwandParameter } from '../lib/leinwandServer';
import KataTafel from '../components/leinwand/KataTafel';
import SiegerBuehne from '../components/leinwand/SiegerBuehne';
import { ehrungAusStand } from '../lib/siegerEhrung';
import { useAnzeigeOptik } from '../hooks/useAnzeigeOptik';
import { BEISPIEL_KATA } from '../lib/anzeigeBeispiele';

const KANAL   = 'tda-formen-display';
const SPEICHER = 'tda-formen-display-state';

export default function FormenDisplayPage() {
  // ?vorschau=1 (Modus-Übersicht): Beispielwertung, ohne Einrichtung,
  // Vollbild und ohne den Stand eines echten Panels zu lesen (22.10.2026)
  const [vorschau] = useState(() => new URLSearchParams(window.location.search).get('vorschau') === '1');
  const [stand, setStand] = useState(() => (vorschau ? BEISPIEL_KATA : null));
  const [vollbild, setVollbild] = useState(false);
  const [setupWeg, setSetupWeg] = useState(vorschau);
  // Optik (Modus-Übersicht → Anzeige / Display, oder ?optik=alt): 'alt' zeigt
  // das bisherige Raster für alle Zustände außer dem Siegerbild.
  const optik = useAnzeigeOptik('kataTafel');
  // Über den Server (18.10.2026): /formen-display?t=<turnier>&m=<matte>&k=<schluessel>
  // läuft auf jedem Gerät; der lokale Weg unten bleibt für denselben Rechner.
  const { fehler: serverFehler } = useLeinwandVomServer('formen', (daten) => { if (daten && daten.typ) setStand(daten); });
  // Fotos nur mit Hallenschlüssel in der eigenen Adresse (t=, k=), 21.10.2026
  const [halle] = useState(() => { const p = leinwandParameter(); return p.turnierId && p.k ? { turnierId: p.turnierId, k: p.k } : null; });

  // Seite fuellt den Bildschirm, nichts scrollt weg
  useEffect(() => {
    const vorher = {
      html: document.documentElement.style.overflow,
      body: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = vorher.html;
      document.body.style.overflow = vorher.body;
    };
  }, []);

  // Auto-Vollbild — klappt, wenn das Fenster aus einer Nutzergeste geoeffnet wurde
  useEffect(() => {
    if (vorschau) return undefined;
    const t = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [vorschau]);

  useEffect(() => {
    const merken = () => setVollbild(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', merken);
    return () => document.removeEventListener('fullscreenchange', merken);
  }, []);

  // Chat-Widget und alles andere ausserhalb der App ausblenden
  useEffect(() => {
    const verstecken = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT', 'STYLE', 'LINK', 'META'].includes(el.tagName)) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    verstecken();
    const beobachter = new MutationObserver(verstecken);
    beobachter.observe(document.body, { childList: true });
    return () => beobachter.disconnect();
  }, []);

  // ── Steuerung empfangen ────────────────────────────────────────────
  useEffect(() => {
    if (vorschau) return undefined;
    const uebernehmen = (roh) => {
      try {
        const daten = typeof roh === 'string' ? JSON.parse(roh) : roh;
        if (daten && daten.typ) setStand(daten);
      } catch (e) { /* unbrauchbare Nachricht ignorieren */ }
    };

    // Beim Oeffnen oder nach einem Reload sofort den letzten Stand holen —
    // die Leinwand soll nicht leer bleiben, nur weil sie spaeter startet.
    try { uebernehmen(localStorage.getItem(SPEICHER)); } catch (e) {}

    const beiSpeicher = (e) => { if (e.key === SPEICHER) uebernehmen(e.newValue); };
    window.addEventListener('storage', beiSpeicher);

    let kanal = null;
    try {
      kanal = new BroadcastChannel(KANAL);
      kanal.onmessage = (e) => uebernehmen(e.data);
    } catch (e) { /* aeltere Browser: localStorage genuegt */ }

    return () => {
      window.removeEventListener('storage', beiSpeicher);
      if (kanal) kanal.close();
    };
  }, [vorschau]);

  const vollbildUmschalten = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
  };

  if (!vollbild && !setupWeg) {
    return <Einrichtung onVollbild={vollbildUmschalten} onWeiter={() => setSetupWeg(true)} />;
  }

  // Siegerbild (21.10.2026): dieselbe Bühne wie /siegerehrung-display
  // (components/leinwand/SiegerBuehne), nicht mehr das alte Raster. Daten wie
  // dort (Platzierung aus der Endwertung), gerechnet wird hier nichts.
  if (stand?.typ === 'sieger') {
    const ehrung = ehrungAusStand(stand);
    return (
      <>
        <SiegerBuehne key={`${ehrung.kategorie_name}-${stand.ehrung_id || ''}`} kategorie={ehrung} halle={halle} />
        {!vorschau && (
          <button className="fdp-vollbild-knopf" onClick={vollbildUmschalten}>
            {vollbild ? 'Vollbild verlassen' : 'Vollbild'}
          </button>
        )}
        <Verbindung fehler={serverFehler} />
      </>
    );
  }

  // Turniertag-Bild (20.10.2026, Muster „Kata“ Ansicht A): Warten, Starter,
  // Wertung, Ergebnis, Rangliste und Disqualifikation laufen auf der Tafel.
  // Das Stechen behält das Raster unten.
  if (stand?.typ !== 'stechen' && optik !== 'alt') {
    const matte = new URLSearchParams(window.location.search).get('m');
    return (
      <>
        <KataTafel stand={stand} matte={matte} halle={halle} />
        {!vorschau && (
          <button className="fdp-vollbild-knopf" onClick={vollbildUmschalten}>
            {vollbild ? 'Vollbild verlassen' : 'Vollbild'}
          </button>
        )}
        <Verbindung fehler={serverFehler} />
      </>
    );
  }

  // Ein Raster fuer alle Zustaende: Kopfband oben, Ranglistenspalte rechts,
  // Fussleiste unten — die Buehne bekommt, was uebrig bleibt. Vorher legte
  // jeder Zustand seinen Inhalt zentriert in die Bildmitte und liess den Rand
  // leer; auf einer 4x3-Meter-Projektion ist das eine Briefmarke an einer
  // dunklen Wand.
  const mitLeiste = zeigtLeiste(stand);
  const ohneFuss = stand?.typ === 'ergebnis';   // der Moment darf ganzflaechig wirken

  return (
    <div className={`fdp ${mitLeiste ? 'mit-leiste' : ''} ${ohneFuss ? 'ohne-fuss' : ''}`}>
      <Kopf stand={stand} />
      <Buehne stand={stand} />
      {mitLeiste && <Leiste stand={stand} />}
      {!ohneFuss && <Fuss stand={stand} />}
      {!vorschau && (
        <button className="fdp-vollbild-knopf" onClick={vollbildUmschalten}>
          {vollbild ? 'Vollbild verlassen' : 'Vollbild'}
        </button>
      )}
      <Verbindung fehler={serverFehler} />
    </div>
  );
}

// Wo die Ranglistenspalte steht. In 'rangliste' und 'sieger' ist die Liste
// selbst der Inhalt der Buehne — dort waere die Spalte eine Dopplung.
const zeigtLeiste = (stand) =>
  ['bereit', 'wertung', 'ergebnis', 'disqualifikation'].includes(stand?.typ || 'bereit')
  && Array.isArray(stand?.rangliste) && stand.rangliste.length > 0;

// Bild, das sich bei einem Ladefehler still zurueckzieht. Das `key` auf der
// Adresse ist wichtig: ohne das behaelt React dasselbe <img>-Element, wenn nur
// die Adresse wechselt — und ein einmal gesetztes display:none aus einem
// frueheren Fehlversuch bliebe kleben, auch wenn das neue Bild laedt.
// ── Kopfzeile ────────────────────────────────────────────────────────
function Kopf({ stand }) {
  const sv = stand?.disziplin === 'sv';
  const titel = {
    wertung:         '点数 WERTUNG',
    ergebnis:        '結果 ERGEBNIS',
    rangliste:       '順位 RANGLISTE',
    stechen:         '同点 STECHEN',
    disqualifikation:'失格 DISQUALIFIKATION',
  }[stand?.typ] || (sv ? '護身 SELBSTVERTEIDIGUNG' : '演武 FORMEN');

  // Ob der Schreiber gespeichert hat, ist Backoffice-Information — das
  // Publikum sieht nur ein Wort, das ihm nichts sagt. Der Indikator steht im
  // Schreiber-Panel, wo er gebraucht wird.
  const rechts = stand?.typ === 'wertung' ? 'LIVE' : stand ? '' : 'BEREIT';

  // Links steht, wer die Veranstaltung ausrichtet: Logo, darunter Turniername
  // und Kategorie. Ohne das koennte die Leinwand von jedem beliebigen
  // Veranstalter sein.
  return (
    <div className="fdp-kopf">
      <div className="fdp-kopf-links">
        <Bild className="fdp-kopf-logo" src={stand?.logo} />
        <div className="fdp-kopf-texte">
          {stand?.turnier && <div className="fdp-kopf-turnier">{stand.turnier}</div>}
          <div className="fdp-kopf-kat">{stand?.kategorie || ''}</div>
        </div>
      </div>
      <div className="fdp-kopf-titel">{titel}</div>
      <div className="fdp-kopf-rechts">
        {stand?.timer?.aktiv && (
          <span className={`fdp-kopf-zeit ${stand.timer.sekunden < 30 && stand.timer.gestartet ? 'knapp' : ''}`}>
            {formatZeit(stand.timer.sekunden)}
          </span>
        )}
        <span className={`fdp-kopf-live ${stand?.typ === 'wertung' ? 'live' : 'wartet'}`}>{rechts}</span>
      </div>
    </div>
  );
}

// ── Buehne: der Zustand entscheidet ──────────────────────────────────
function Buehne({ stand }) {
  if (!stand || stand.typ === 'bereit') return <Bereit stand={stand} />;
  if (stand.typ === 'wertung')   return <Wertung stand={stand} />;
  if (stand.typ === 'ergebnis')  return <Ergebnis stand={stand} />;
  if (stand.typ === 'rangliste') return <Rangliste stand={stand} />;
  if (stand.typ === 'stechen')   return <Stechen stand={stand} />;
  if (stand.typ === 'disqualifikation') return <Disqualifikation stand={stand} />;
  return <Bereit stand={stand} />;
}

function Bereit({ stand }) {
  const timer = stand?.timer;
  return (
    <div className="fdp-buehne">
      {/* Wasserzeichen, nicht Bildmitte: es sass vorher mitten ueber dem Namen */}
      <div className="fdp-kanji wasserzeichen">演武</div>
      {stand?.teilnehmer ? (
        <>
          <div className="fdp-startnr">#{stand.teilnehmer.startnummer}</div>
          <div className="fdp-name">{stand.teilnehmer.vorname} {stand.teilnehmer.nachname}</div>
          <div className="fdp-unterzeile">
            <span>{stand.teilnehmer.verein_name || ''}</span>
            <Gurt farbe={stand.teilnehmer.gurtfarbe} />
          </div>
        </>
      ) : (
        <div className="fdp-bereit-text">BEREIT</div>
      )}
      {timer?.aktiv && (
        <div className="fdp-timer">
          <div className="fdp-timer-lbl">ZEIT</div>
          <div className={`fdp-timer-wert ${timer.sekunden < 30 && timer.gestartet ? 'knapp' : ''}`}>
            {formatZeit(timer.sekunden)}
          </div>
        </div>
      )}
    </div>
  );
}

function Wertung({ stand }) {
  const noten = stand.wertungen || [];
  const gestrichen = stand.scoring?.gestrichen || [];
  const vollstaendig = !!stand.scoring?.vollstaendig;
  // Bis der letzte Kampfrichter eingetragen hat, steht auf der Leinwand nur,
  // von wem schon etwas da ist — nicht was. Danach erscheinen alle Zahlen
  // gleichzeitig. Die Zahlen kommen bis dahin gar nicht erst hier an.
  //
  // In der Selbstverteidigung ist das anders: dort wertet EINE Person Uebung
  // fuer Uebung, es gibt keine Kampfrichter, die sich beeinflussen koennten.
  // Die Noten erscheinen deshalb sofort.
  const sv = stand.disziplin === 'sv';
  const kachelLabel = sv ? 'Übung' : 'KR';
  const eingegangen = noten.filter(w => w.eingegangen).length;

  return (
    <div className="fdp-buehne">
      <div className="fdp-startnr">#{stand.teilnehmer?.startnummer}</div>
      <div className="fdp-name">{stand.teilnehmer?.vorname} {stand.teilnehmer?.nachname}</div>
      <div className="fdp-unterzeile">
        <span>{stand.teilnehmer?.verein_name || ''}</span>
        <Gurt farbe={stand.teilnehmer?.gurtfarbe} />
      </div>

      <div className={`fdp-noten ${vollstaendig ? 'enthuellt' : ''}`}>
        {noten.map((w, i) => (
          <div
            key={i}
            className={`fdp-note ${w.eingegangen ? 'gefuellt' : ''} ${gestrichen.includes(i + 1) ? 'gestrichen' : ''}`}
          >
            <div className="fdp-note-lbl">
              {gestrichen.includes(i + 1) ? 'STREICH' : `${kachelLabel} ${i + 1}`}
            </div>
            <div className="fdp-note-wert">
              {(vollstaendig || sv)
                ? (w.punkte !== '' && w.punkte !== null ? w.punkte : '—')
                : (w.eingegangen ? <span className="fdp-note-bereit">●</span> : '—')}
            </div>
          </div>
        ))}
      </div>
      {!vollstaendig && !sv && (
        <div className="fdp-eingegangen">{eingegangen} von {noten.length} Wertungen eingegangen</div>
      )}

      <div className={`fdp-summe ${stand.gespeichert ? '' : 'pulsiert'}`}>
        <div className="fdp-summe-lbl">{stand.scoring?.gross_label || '合計 GESAMT'}</div>
        <div className="fdp-summe-wert">
          {(vollstaendig || sv) ? Number(stand.scoring?.gesamt_punkte || 0).toFixed(2) : '—'}
        </div>
        <div className="fdp-summe-sub">{stand.scoring?.unterzeile || ''}</div>
      </div>

      {stand.gespeichert && stand.platzierung && (
        <div className={`fdp-platz ${stand.platzierung > 3 ? 'ausser' : ''}`}>
          <Platz platz={stand.platzierung} /> Platz {stand.platzierung}
        </div>
      )}

      {/* Der Kasten "Naechster Wettkaempfer" ist entfallen: seit die Fussleiste
          die naechsten beiden Starter zeigt, stand er doppelt im Bild — und bei
          1280x720 schob er den Athletenblock ueber das Kopfband hinaus. */}
    </div>
  );
}

function Ergebnis({ stand }) {
  return (
    // "結果 ERGEBNIS" stand hier UND in der Kopfzeile. Statt der Dopplung
    // zeigt der Zustand jetzt, was ihm fehlte: Startnummer, Verein und die
    // Platzierung.
    <div className="fdp-buehne">
      <div className="fdp-startnr">#{stand.teilnehmer?.startnummer}</div>
      <div className="fdp-name">{stand.teilnehmer?.vorname} {stand.teilnehmer?.nachname}</div>
      <div className="fdp-unterzeile">
        <span>{stand.teilnehmer?.verein_name || ''}</span>
        <Gurt farbe={stand.teilnehmer?.gurtfarbe} />
      </div>
      <div className="fdp-ergebnis-wert">{Number(stand.scoring?.gesamt_punkte || 0).toFixed(2)}</div>
      <div className="fdp-ergebnis-einheit">PUNKTE</div>
      <div className="fdp-ergebnis-weg">{stand.scoring?.unterzeile || ''}</div>
      {stand.platzierung && (
        <div className={`fdp-platz gross ${stand.platzierung > 3 ? 'ausser' : ''}`}>
          <Platz platz={stand.platzierung} /> Platz {stand.platzierung}
        </div>
      )}
    </div>
  );
}

// To-do 496: Plätze tauschen per FLIP, eine Zeile mit neuer Wertung oder
// neuem Platz leuchtet kurz auf. Schlüssel ist die Teilnehmer-ID, nicht die
// Position — sonst sähe jeder Tausch wie eine Änderung aller Zeilen aus.
const ranglistenSchluessel = (tn, i) => tn.wettkaempfer_id ?? `i${i}`;
const ranglistenSignatur = (tn, i) => `${i}|${Number(tn.gesamt_punkte) || 0}|${tn.status || ''}`;

function Rangliste({ stand }) {
  const liste = (stand.rangliste || []).slice(0, 10);
  const anim = useAnimierteListe(liste, ranglistenSchluessel, ranglistenSignatur);
  let platz = 0;
  return (
    <div className="fdp-buehne">
      <div className="fdp-liste" ref={anim.behaelterRef}>
        {anim.zeilen.map((z) => { const tn = z.eintrag; const i = z.weg ? 99 : platz++; return (
          // Kein anim-ein hier: die Zeilen haben schon ihr eigenes Hereingleiten (fdpHerein)
          <div key={z.schluessel} data-flip={z.schluessel}
               className={`fdp-liste-zeile ${i < 3 ? 'p' + (i + 1) : ''}${z.weg ? ' anim-weg' : ''}${z.markiert ? ' anim-leuchten' : ''}`}>
            <span className="fdp-liste-pos">{!z.weg && <Platz platz={i + 1} />}</span>
            <span className="fdp-liste-name">{tn.vorname} {tn.nachname}</span>
            <span className="fdp-liste-verein">{tn.verein_name || ''}</span>
            <span className="fdp-liste-punkte">{Number(tn.gesamt_punkte || 0).toFixed(2)}</span>
          </div>
        ); })}
      </div>
    </div>
  );
}

// Fussleiste: feste Hoehe, in jedem Zustand an derselben Stelle.
// Sie beantwortet die zwei Fragen, die in der Halle wirklich gestellt werden:
// wie weit ist die Kategorie, und wer kommt als naechstes dran.
function Fuss({ stand }) {
  const f = stand?.fortschritt;
  const folgende = stand?.folgende || [];
  return (
    <div className="fdp-fuss">
      {f && (
        <div className="fdp-fuss-fortschritt">
          <span className="fdp-fuss-lbl">Starter</span>
          <span className="fdp-fuss-zahl">{f.position}</span>
          <span className="fdp-fuss-von">von</span>
          <span className="fdp-fuss-zahl">{f.gesamt}</span>
        </div>
      )}
      {folgende.length > 0 && (
        <div className="fdp-fuss-folgende">
          <span className="fdp-fuss-lbl">Als Nächstes</span>
          {folgende.map((t, i) => (
            <span key={i} className="fdp-fuss-name">
              <span className="fdp-fuss-nr">#{t.startnummer}</span> {t.vorname} {t.nachname}
            </span>
          ))}
        </div>
      )}
      {/* Sponsorenband rechts. Fehlt es, bleibt die Stelle leer — das Raster
          darf deswegen nicht springen. */}
      <Bild className="fdp-fuss-banner" src={stand?.banner} />
    </div>
  );
}

// Stechen: in der Halle die wichtigste Erklaerung ueberhaupt — sonst steht
// eine Rangliste, und es geht ohne erkennbaren Grund weiter.
function Stechen({ stand }) {
  const beteiligte = stand.beteiligte || [];
  return (
    <div className="fdp-buehne">
      <div className="fdp-stechen-lbl">Punktgleichheit</div>
      {stand.platzierung && (
        <div className="fdp-stechen-platz">um Platz {stand.platzierung}</div>
      )}
      <div className="fdp-stechen-namen">
        {beteiligte.map((tn, i) => (
          <React.Fragment key={tn.wettkaempfer_id || i}>
            {i > 0 && <span className="fdp-stechen-gegen">gegen</span>}
            <div className="fdp-stechen-person">
              <div className="fdp-name">{tn.vorname} {tn.nachname}</div>
              <div className="fdp-unterzeile"><span>{tn.verein_name || ''}</span></div>
              <div className="fdp-stechen-punkte">{Number(tn.gesamt_punkte || 0).toFixed(2)}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="fdp-stechen-hinweis">Entscheidung durch die Kampfrichter</div>
    </div>
  );
}

// Disqualifikation: Name und Tatsache, mehr nicht. Der Grund steht im
// Protokoll und geht das Publikum nichts an.
function Disqualifikation({ stand }) {
  return (
    <div className="fdp-buehne">
      {stand.teilnehmer?.startnummer && <div className="fdp-startnr">#{stand.teilnehmer.startnummer}</div>}
      <div className="fdp-name">{stand.teilnehmer?.vorname} {stand.teilnehmer?.nachname}</div>
      <div className="fdp-unterzeile"><span>{stand.teilnehmer?.verein_name || ''}</span></div>
      <div className="fdp-dq-wort">DISQUALIFIZIERT</div>
    </div>
  );
}

function Leiste({ stand }) {
  const anim = useAnimierteListe(stand.rangliste || [], ranglistenSchluessel, ranglistenSignatur);
  let platz = 0;
  return (
    <div className="fdp-leiste" ref={anim.behaelterRef}>
      <div className="fdp-leiste-titel">順位 Aktuelle Rangliste</div>
      {anim.zeilen.map((z) => {
        const tn = z.eintrag;
        const i = z.weg ? null : platz++;
        const punkte = Number(tn.gesamt_punkte) || 0;
        const aktuell = tn.wettkaempfer_id === stand.teilnehmer?.wettkaempfer_id;
        return (
          <div key={z.schluessel} data-flip={z.schluessel} className={`fdp-leiste-zeile ${aktuell ? 'aktuell' : ''} ${anim.klasse(z)}`}>
            <span className="fdp-leiste-platz">{i != null && <Platz platz={i + 1} />}</span>
            <span className="fdp-leiste-name">{kurzerName(tn.vorname, tn.nachname)}</span>
            <span className={`fdp-leiste-punkte ${tn.status === 'completed' ? 'fertig' : ''}`}>
              {punkte > 0 ? punkte.toFixed(2) : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Einrichtungs-Hinweis, solange kein Vollbild laeuft ───────────────
function Einrichtung({ onVollbild, onWeiter }) {
  const [kopiert, setKopiert] = useState(false);
  // Mit Turnier, Matte und Schlüssel, wenn die Seite so geöffnet wurde
  const adresse = `${window.location.origin}/formen-display${window.location.search}`;

  const kopieren = () => {
    navigator.clipboard.writeText(adresse).then(() => {
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2500);
    }).catch(() => {});
  };

  return (
    <div className="fdp-setup">
      <div className="fdp-setup-karte">
        <div className="fdp-setup-titel">演武 Leinwand für die Formen-Wertung</div>
        <div className="fdp-setup-text">
          Diese Seite zeigt, was im Schreiber-Panel eingetragen wird, auch auf einem
          anderen Rechner am Beamer. Dafür diese Adresse dort öffnen (sie kommt mit
          Turnier, Matte und Schlüssel aus „Leinwand öffnen“ im Panel oder von der Gerätekarte):
        </div>
        <code className="fdp-setup-url">{adresse}</code>
        <div className="fdp-setup-knoepfe">
          <button className="fdp-knopf" onClick={onVollbild}>Vollbild starten</button>
          <button className="fdp-knopf leise" onClick={kopieren}>
            {kopiert ? 'Adresse kopiert' : 'Adresse kopieren'}
          </button>
          <button className="fdp-knopf leise" onClick={onWeiter}>Ohne Vollbild weiter</button>
        </div>
      </div>
    </div>
  );
}

// Gurtfarben. "Braungurt" als grauer Text in einem grauen Pill ist aus zwanzig
// Metern nicht lesbar — ein Pill IN der Gurtfarbe schon, auch ohne das Wort.
const GURT_FARBEN = [
  [/(weiss|weiß|white)/i,              { grund: '#f2f2f2', schrift: '#1a1510' }],
  [/(gelb|yellow)/i,                   { grund: '#f2c832', schrift: '#1a1510' }],
  [/(orange)/i,                        { grund: '#e8892b', schrift: '#1a1510' }],
  [/(gr[üu]n|green)/i,                 { grund: '#3f9b52', schrift: '#fff' }],
  [/(blau|blue)/i,                     { grund: '#2f6fb5', schrift: '#fff' }],
  [/(lila|violett|purple)/i,           { grund: '#7a4fa3', schrift: '#fff' }],
  [/(braun|brown)/i,                   { grund: '#7a4a23', schrift: '#fff' }],
  [/(rot|red)/i,                       { grund: '#b3312c', schrift: '#fff' }],
  [/(schwarz|black|dan|meister)/i,     { grund: '#141414', schrift: '#f0d98a' }],
];

function Gurt({ farbe }) {
  if (!farbe) return null;
  const treffer = GURT_FARBEN.find(([muster]) => muster.test(farbe));
  const stil = treffer
    ? { background: treffer[1].grund, color: treffer[1].schrift, borderColor: 'rgba(255,255,255,0.35)' }
    : undefined;
  return <span className="fdp-gurt" style={stil}>{farbe}</span>;
}

// In der schmalen Seitenleiste wird der VORNAME abgekuerzt, nicht der
// Nachname: "Maximilian Aschenbrenner" lief dort in ellipsis, und
// ausgerechnet der Nachname verschwand — bei dem Athleten, der gerade
// gewertet wird.
const kurzerName = (vorname, nachname) => {
  const v = (vorname || '').trim();
  const n = (nachname || '').trim();
  if (!v) return n;
  if (!n) return v;
  return `${v[0]}. ${n}`;
};

const formatZeit = (sekunden) => {
  const s = Math.max(0, Number(sekunden) || 0);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
